import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

export const TASK_DATE_RANGE_OPTIONS = ['7d', '30d', 'all'] as const;
export type TaskDateRange = (typeof TASK_DATE_RANGE_OPTIONS)[number];

export const inboxSearchSchema = z.object({
  tab: z.enum(['home', 'inbox', 'tasks', 'agents']).optional(),
  search: z.string().optional(),
  filter: z.enum(['all', 'escalations', 'notifications']).optional(),
  escalationFilter: z
    .enum(['all', 'needs-input', 'approvals', 'failures'])
    .optional(),
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
  // Inverted: the inbox shows read notifications by default, so we only persist
  // the non-default "hide read" state. `setParams` strips falsy values, so a
  // default-on `showRead` couldn't round-trip its off state — `hideRead` only
  // ever stores `true`.
  hideRead: z.boolean().optional(),
  selId: z.string().optional(),
  statusFilter: z
    .enum([
      'active',
      'in_progress',
      'needs_input',
      'pending',
      'failed',
      'completed',
      'cancelled',
    ])
    .optional(),
  assigneeFilter: z.string().optional(),
  taskDateRange: z.enum(TASK_DATE_RANGE_OPTIONS).optional(),
  taskSearch: z.string().optional(),
  taskId: z.string().optional(),
  agentId: z.string().optional(),
});

export type InboxSearchParams = z.infer<typeof inboxSearchSchema>;

export function useInboxSearchParams() {
  const params = useSearch({ strict: false }) as InboxSearchParams;
  const navigate = useNavigate();

  const setParams = useCallback(
    (updates: Partial<InboxSearchParams>) => {
      navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...updates };
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined || v === null || v === '' || v === false)
              delete next[k];
          }
          return next;
        }) as never,
        replace: true,
      });
    },
    [navigate],
  );

  return { params, setParams };
}
